import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  deleteUser,
} from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { sendTestWorkerCommand } from '../runtime/mobile/testWorkerClient';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const [isOwner, setIsOwner] = useState(false);
  const profileUnsubRef = useRef(null);

  useEffect(() => {
    const stopProfile = () => {
      if (profileUnsubRef.current) {
        profileUnsubRef.current();
        profileUnsubRef.current = null;
      }
    };
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      stopProfile();
      setUser(firebaseUser);
      if (firebaseUser) {
        try {
          const tokenResult = await firebaseUser.getIdTokenResult();
          setIsOwner(tokenResult.claims?.owner === true);
        } catch {
          setIsOwner(false);
        }
        // Live subscription so profile changes made server-side (activation,
        // Telegram linking, invite acceptance) reach the UI without a reload.
        profileUnsubRef.current = onSnapshot(
          doc(db, 'users', firebaseUser.uid),
          (snap) => {
            setProfile(snap.exists() ? snap.data() : null);
            setLoading(false);
          },
          () => setLoading(false),
        );
      } else {
        setProfile(null);
        setIsOwner(false);
        setLoading(false);
      }
    });
    return () => {
      stopProfile();
      unsubscribe();
    };
  }, []);

  // Invite-only onboarding. The user creates their auth account, then the
  // server (accept-invite command) validates the signed invite and writes the
  // inactive profile document — there is no client-side profile write. If the
  // server rejects the invite, the just-created auth account is rolled back so
  // the link can be retried.
  const acceptInvitation = async ({ token, email, password, fullName }) => {
    const cred = await createUserWithEmailAndPassword(auth, email.trim(), password);
    try {
      await sendTestWorkerCommand({
        user: cred.user,
        path: '/commands/accept-invite',
        body: { token: String(token).trim(), fullName: fullName.trim(), email: email.trim() },
        idempotencyKey: `accept-invite:${cred.user.uid}`,
      });
    } catch (e) {
      try { await deleteUser(cred.user); } catch { /* best effort rollback */ }
      throw e;
    }
  };

  const login = (email, password) => signInWithEmailAndPassword(auth, email, password);

  const logout = () => signOut(auth);

  const resetPassword = (email) => sendPasswordResetEmail(auth, email);

  const isAdmin = profile?.role === 'admin' || isOwner;

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        loading,
        acceptInvitation,
        login,
        logout,
        resetPassword,
        isAdmin,
        isOwner,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext) || {};
